import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void>;
  loading?: boolean;
}

export function ChaoReportReturnModal({ open, onClose, onConfirm, loading }: Props) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = note.trim();
    if (!trimmed) {
      setError("Informe o motivo da devolução.");
      return;
    }
    setError(null);
    try {
      await onConfirm(trimmed);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível devolver o registro.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-50">
            <RotateCcw className="h-4 w-4 text-amber-700" />
          </div>
          <span className="font-semibold text-slate-800">Devolver registro</span>
        </div>
      }
      size="md"
      elevated
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="secondary" onClick={() => void handleConfirm()} loading={loading}>
            Devolver para ajuste
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          O registro volta para o campo. O avanço informado não será computado até uma nova aprovação.
        </p>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="chao-devolver-motivo"
            className="text-xs font-semibold text-slate-500 uppercase tracking-wide"
          >
            Motivo da devolução *
          </label>
          <textarea
            id="chao-devolver-motivo"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (error) setError(null);
            }}
            rows={4}
            autoFocus
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
            placeholder="Descreva o que precisa ser corrigido nas fotos ou no avanço físico"
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
